/**
 * BalanceApp Analytics and Tracking Module
 * Handles user behavior tracking, performance monitoring, and analytics
 */

import { apiFetch, BACKEND_URL } from "./config/apiConfig";

class BalanceAppTracking {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
    this.events = [];
    this.isEnabled = true;
    this.batchSize = 10;
    this.flushInterval = 30000; // 30 seconds
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second

    // Initialize tracking
    this.init();
  }

  init() {
    // Start periodic flush
    setInterval(() => {
      this.flush();
    }, this.flushInterval);

    // Flush on page unload
    window.addEventListener("beforeunload", () => {
      this.flush(true);
    });

    // Track page visibility changes
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.trackEvent("page_hidden");
      } else {
        this.trackEvent("page_visible");
      }
    });
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  isTrackingEnabled() {
    // Check if user has opted out
    const optOut = localStorage.getItem("balanceapp_tracking_opt_out");
    return this.isEnabled && !optOut;
  }

  trackEvent(eventName, properties = {}) {
    if (!this.isTrackingEnabled()) return;

    const event = {
      id: `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sessionId: this.sessionId,
      eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        sessionDuration: Date.now() - this.sessionStartTime,
      },
    };

    this.events.push(event);

    // Auto-flush if batch size reached
    if (this.events.length >= this.batchSize) {
      this.flush();
    }
  }

  // User Authentication Events
  trackMagicLinkRequest(email) {
    this.trackEvent("magic_link_request", { email });
  }

  trackAuthentication(email, success) {
    this.trackEvent("authentication", { email, success });
  }

  trackUserRegistration(email) {
    this.trackEvent("user_registration", { email });
  }

  trackLogout() {
    this.trackEvent("logout");
  }

  // Bias Check Events
  trackBiasCheck(url, domain, biasScore, credibilityScore, userEmail = null) {
    this.trackEvent("bias_check", {
      url,
      domain,
      biasScore,
      credibilityScore,
      userEmail,
    });
  }

  trackBiasCheckError(url, error) {
    this.trackEvent("bias_check_error", { url, error });
  }

  // Article Analysis Events
  trackArticleAnalysis(url, biasScore, credibilityScore, analysisType) {
    this.trackEvent("article_analysis", {
      url,
      biasScore,
      credibilityScore,
      analysisType,
    });
  }

  // News Aggregator Events
  trackNewsPageView(filters = {}) {
    this.trackEvent("news_page_view", { filters });
  }

  trackStoryClick(storyId, storyTitle, position) {
    this.trackEvent("story_click", { storyId, storyTitle, position });
  }

  trackFilterChange(filterType, filterValue) {
    this.trackEvent("filter_change", { filterType, filterValue });
  }

  // Admin Events
  trackAdminAction(action, details = {}) {
    this.trackEvent("admin_action", { action, ...details });
  }

  trackAdminDashboardAccess() {
    this.trackEvent("admin_dashboard_access");
  }

  trackFeatureToggle(featureName, enabled) {
    this.trackEvent("feature_toggle", { featureName, enabled });
  }

  trackRssUpdate(feedCount, articlesProcessed) {
    this.trackEvent("rss_update", { feedCount, articlesProcessed });
  }

  trackUserDelete(email) {
    this.trackEvent("user_delete", { email });
  }

  // Performance Events
  trackPageLoad(loadTime) {
    this.trackEvent("page_load", { loadTime });
  }

  trackApiCall(endpoint, method, responseTime, statusCode) {
    this.trackEvent("api_call", {
      endpoint,
      method,
      responseTime,
      statusCode,
    });
  }

  // Error Events
  trackError(error, context = {}) {
    this.trackEvent("error", {
      message: error.message,
      stack: error.stack,
      ...context,
    });
  }

  // Session Management
  startSession() {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = Date.now();
    this.trackEvent("session_start");
  }

  endSession() {
    this.trackEvent("session_end", {
      sessionDuration: Date.now() - this.sessionStartTime,
    });
    this.flush(true);
  }

  // Feature Usage Events
  trackFeatureUsage(featureName, details = {}) {
    this.trackEvent("feature_usage", { featureName, ...details });
  }

  // Search Events
  trackSearch(query, results, filters = {}) {
    this.trackEvent("search", { query, results, filters });
  }

  // Data Export
  async flush(immediate = false) {
    if (this.events.length === 0) return;

    const eventsToSend = [...this.events];
    this.events = [];

    try {
      await this.sendEvents(eventsToSend, immediate);
    } catch (error) {
      // Re-add events to queue for retry
      this.events.unshift(...eventsToSend);
      console.warn("Failed to send tracking events:", error);
    }
  }

  async sendEvents(events, immediate = false) {
    if (!this.isTrackingEnabled()) return;

    const payload = {
      sessionId: this.sessionId,
      events,
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        immediate,
      },
    };

    let retries = 0;
    while (retries < this.maxRetries) {
      try {
        const response = await fetch(BACKEND_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            url: "internal://tracking",
            tracking_data: payload,
          }),
        });

        if (response.ok) {
          return; // Success
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        retries++;
        if (retries >= this.maxRetries) {
          throw error;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, this.retryDelay * retries)
        );
      }
    }
  }

  // Utility Methods
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      sessionStartTime: this.sessionStartTime,
      sessionDuration: Date.now() - this.sessionStartTime,
      eventsQueued: this.events.length,
    };
  }

  optOut() {
    localStorage.setItem("balanceapp_tracking_opt_out", "true");
    this.isEnabled = false;
    this.events = [];
  }

  optIn() {
    localStorage.removeItem("balanceapp_tracking_opt_out");
    this.isEnabled = true;
  }

  // Debug Methods
  getQueuedEvents() {
    return [...this.events];
  }

  clearQueue() {
    this.events = [];
  }
}

// Create singleton instance
const balanceAppTracking = new BalanceAppTracking();

// Export for use in components
export default balanceAppTracking;

// Also export the class for testing
export { BalanceAppTracking };
